'use client';
import React, { useEffect } from "react";
import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import { PortableText as PortableTextComponent } from "@portabletext/react";
import { urlForImage } from "@/lib/sanity/image";
import Iframe from "react-iframe";
import getVideoId from "get-video-id";
import { cx } from "@/utils/all";

import {Refractor} from 'react-refractor'
import js from "refractor/javascript";
import jsx from "refractor/jsx";
import html from "refractor/markup";
import css from "refractor/css";
import bash from "refractor/bash";

// Hook pour enregistrer les langages côté client
const useRegisterLanguages = () => {
  useEffect(() => {
    if (!Refractor) return;
    Refractor.registerLanguage(js);
    Refractor.registerLanguage(jsx);
    Refractor.registerLanguage(html);
    Refractor.registerLanguage(css);
    Refractor.registerLanguage(bash);
  }, []);
};

// ---------------------------
// Components PortableText
// ---------------------------

// Image lazy-loaded
const ImageComponent = ({ value }) => (
    <Image
        src={urlForImage(value)}
        alt={value.alt || "Image"}
        loading="lazy"
        className="object-cover"
        sizes="(max-width: 800px) 100vw, 800px"
    />
);

// Table
const PortableTextTable = ({ value }) => {
  const [head, ...rows] = value.table.rows;
  return (
      <table>
        {head.cells.filter(Boolean).length > 0 && (
            <thead>
            <tr>
              {head.cells.map((cell) => (
                  <th key={cell}>{cell}</th>
              ))}
            </tr>
            </thead>
        )}
        <tbody>
        {rows.map((row, idx) => (
            <tr key={idx}>
              {row.cells.map((cell, cidx) => (
                  <td key={cidx}>{cell}</td>
              ))}
            </tr>
        ))}
        </tbody>
      </table>
  );
};

// Code avec syntax highlighting
const Code = ({ value }) => {
  useRegisterLanguages();

  if (!Refractor) return null; // Évite le rendu SSR

  return (
      <Refractor
          language={value.language || "bash"}
          value={value.code}
          markers={value.highlightedLines}
      />
  );
};

// Iframe / vidéo
const IframePreview = ({ value }) => {
  const { url, height } = value;
  if (!url) return <p>Missing Embed URL</p>;

  const { id, service } = getVideoId(url);
  const finalURL =
      id && service === "youtube"
          ? `https://www.youtube-nocookie.com/embed/${id}`
          : url;

  return (
      <Iframe
          url={finalURL}
          width="100%"
          height={height || "350"}
          className={cx(!height && "aspect-video", "rounded-md")}
          display="block"
          position="relative"
          frameBorder="0"
          allowFullScreen
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture"
      />
  );
};

// ---------------------------
// Définition des components pour PortableText
// ---------------------------
const components = {
  types: {
    image: ImageComponent,
    code: Code,
    embed: IframePreview,
    tables: PortableTextTable,
  },
  marks: {
    center: (props) => <div className="text-center">{props.children}</div>,
    highlight: (props) => (
        <span className="font-bold text-blue-500">{props.children}</span>
    ),
    link: ({ children, value }) => {
      const rel = !value.href.startsWith("/") ? "noopener" : undefined;
      const target = !value.href.startsWith("/") ? "_blank" : undefined;
      return (
          <a href={value.href} rel={rel} target={target}>
            {children}
          </a>
      );
    },
    internalLink: ({ children, value }) => (
        <Link href={`/article/${value?.slug?.current}`}>{children}</Link>
    ),
  },
};

// ---------------------------
// Export PortableText
// ---------------------------
export const PortableText = (props) => (
    <PortableTextComponent components={components} {...props} />
);
